import { useCallback, useMemo } from 'react';
import { useOrganization } from '@context/OrganizationContext';
import { useContextOrganizationId } from '@context/OrganizationContext/useContextOrganizationId';
import type { NavigationItemProps } from '@goodfynd/layout';
import { useOrganizationFeatureAccess } from '@hooks/useFeatureAccess';
import { getOrganizationRoutePath } from 'utils/routes-util';

import routes from '../../../config/routes';

export const useOrganizationNavItems = (): NavigationItemProps[] => {
  const { isFetching } = useOrganization();
  const organizationNameId = useContextOrganizationId();

  const {
    hasMasterCatalogAccess: hasMasterCatalogAccess,
    hasQuotesAccess: hasQuotesAccess,
  } = useOrganizationFeatureAccess();

  const isBusy = useMemo(() => isFetching, [isFetching]);

  const createNavItem = useCallback(
    (item: NavigationItemProps) => {
      return {
        ...item,
        isBusy,
      };
    },
    [isBusy]
  );

  return useMemo(
    () => [
      createNavItem({
        href: getOrganizationRoutePath(
          routes.organization.overview,
          organizationNameId
        ),
        icon: 'star',
        label: 'Overview',
      }),
      // createNavItem({
      //   icon: 'organization',
      //   label: 'Manage',
      //   subItems: [
      //     createNavItem({
      //       href: getOrganizationRoutePath(
      //         routes.organization.manage.organizations,
      //         organizationNameId
      //       ),
      //       label: 'Organizations',
      //     }),
      //     createNavItem({
      //       href: getOrganizationRoutePath(
      //         routes.organization.manage.territories,
      //         organizationNameId
      //       ),
      //       label: 'Territories',
      //     }),
      //     createNavItem({
      //       href: getOrganizationRoutePath(
      //         routes.organization.manage.vendors,
      //         organizationNameId
      //       ),
      //       label: 'Vendors',
      //     }),
      //     createNavItem({
      //       href: getOrganizationRoutePath(
      //         routes.organization.manage.staff,
      //         organizationNameId
      //       ),
      //       label: 'Staff',
      //     }),
      //   ],
      // }),
      createNavItem({
        icon: 'calendar',
        label: 'Schedule',
        subItems: [
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.schedule.home,
              organizationNameId
            ),
            label: 'List',
          }),
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.schedule.map,
              organizationNameId
            ),
            label: 'Map',
          }),
        ],
      }),
      // {
      //   href: getOrganizationRoutePath(
      //     routes.organization.events,
      //     organizationNameId
      //   ),
      //   icon: 'location_pin',
      //   label: 'Events',
      // },
      // {
      //   href: getOrganizationRoutePath(
      //     routes.organization.promos,
      //     organizationNameId
      //   ),
      //   icon: 'coupon',
      //   label: 'Promo codes',
      // },
      createNavItem({
        id: 'transactions',
        icon: 'order',
        label: 'Orders',
        subItems: [
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.transactions.online,
              organizationNameId
            ),
            label: 'Online',
          }),
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.transactions.walkups,
              organizationNameId
            ),
            label: 'Walkups',
          }),
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.transactions.all,
              organizationNameId
            ),
            label: 'All',
          }),
        ],
      }),
      createNavItem({
        icon: 'invoice',
        label: 'Invoicing',
        subItems: [
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.invoicing.quotes,
              organizationNameId
            ),
            isBlocked: !hasQuotesAccess,
            label: 'Quotes',
          }),
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.invoicing.invoices,
              organizationNameId
            ),
            label: 'Invoices',
          }),
        ],
      }),
      createNavItem({
        id: 'products',
        href: getOrganizationRoutePath(
          routes.organization.products.items,
          organizationNameId
        ),
        icon: 'fork_knife',
        isBlocked: !hasMasterCatalogAccess,
        label: 'Products',
      }),
      createNavItem({
        href: getOrganizationRoutePath(
          routes.organization.info.home,
          organizationNameId
        ),
        icon: 'organization',
        label: 'Organization',
      }),
      createNavItem({
        icon: 'reports',
        label: 'Reports',
        subItems: [
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.reports.transactions.tickets,
              organizationNameId
            ),
            label: 'Transactions',
          }),
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.reports.payouts,
              organizationNameId
            ),
            label: 'Payouts',
          }),
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.reports.advanced,
              organizationNameId
            ),
            label: 'Summary',
          }),
        ],
      }),
      createNavItem({
        icon: 'settings',
        label: 'Settings',
        subItems: [
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.settings.account,
              organizationNameId
            ),
            label: 'Account',
          }),
          createNavItem({
            href: getOrganizationRoutePath(
              routes.organization.settings.ordering,
              organizationNameId
            ),
            label: 'Ordering',
          }),
        ],
      }),
      // {
      //   href: routes.docs,
      //   icon: 'help',
      //   label: 'Support',
      // },
    ],
    [createNavItem, hasMasterCatalogAccess, hasQuotesAccess, organizationNameId]
  );
};
